/**
 * Tee packs: the PACK row rendered by snippets/pack-offer.liquid.
 *
 * Pack 1 leaves the page as it is. Pack 2 or 3 opens a picker per extra tee, relabels the
 * add-to-cart button and the sticky bar's button ("ADD 2 TO BAG — $88"), and gives
 * assets/product-form.js the items for one /cart/add.js call (getItems). The store's automatic
 * discount prices them; nothing here does any pricing.
 *
 * The theme morphs both buttons from server HTML on every variant:update, so the pack label is
 * re-applied after each one. `#base` is the theme's own state from just before that, which is
 * what pack 1 writes back. The pack label replaces ADD TO BAG and Pre-order; a sold-out TEE 1
 * stays exactly as the theme rendered it. When any tee in the pack is a pre-order size the label
 * reads "PREORDER 2 — $88" and the button gets the pre-order marker (its orange styling).
 *
 * After every apply it fires `pack:change` on document, for the mobile button in
 * sections/product-information.liquid, which copies the real button.
 */
class PackPicker extends HTMLElement {
  #pack = 1;

  /** @type {Array<{id: number, available: boolean, options: string[]}>} */
  #variants = [];

  /** Extra tees the shopper has changed by hand. Untouched ones copy TEE 1 when they open. */
  #touched = new Set();

  /** @type {{main?: ButtonState, sticky?: ButtonState}} */
  #base = {};

  #active = false;

  /** @type {Record<string, boolean> | undefined} */
  #preorderStates;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-pack-variants]')?.textContent || '[]');
    } catch (error) {
      this.#variants = [];
    }

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('change', this.#onChange, { signal });
    this.#section()?.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    this.#convert();
    this.#snapshot();
    this.#apply();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * The cart lines for the selected pack, or null when the pack isn't in play (pack 1, or TEE 1
   * can't be bought), in which case the normal single add runs. An empty array means a pack is
   * selected but can't be added; the button is disabled then anyway. Line item properties are
   * added by assets/product-form.js.
   * @param {string | number} tee1Id - The variant chosen in the main picker.
   * @returns {Array<{variantId: string, quantity: number}> | null}
   */
  getItems(tee1Id) {
    if (!this.#active || !tee1Id) return null;

    const ids = this.#packIds(tee1Id);
    if (!ids) return [];

    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const id of ids) quantities.set(id, (quantities.get(id) || 0) + 1);

    return Array.from(quantities, ([id, quantity]) => ({ variantId: String(id), quantity }));
  }

  /**
   * Every tee in the selected pack, TEE 1 first, or null when an open picker is on a size that
   * can't be bought.
   * @param {string | number} tee1Id
   * @returns {number[] | null}
   */
  #packIds(tee1Id) {
    const ids = [Number(tee1Id)];
    for (const tee of this.#openTees()) {
      const variant = this.#teeVariant(tee);
      if (!variant?.available) return null;
      ids.push(variant.id);
    }
    // Default-variant-only products have no pickers: the pack is the same variant N times.
    while (ids.length < this.#pack) ids.push(Number(tee1Id));
    return ids;
  }

  /**
   * Whether any of these tees is a pre-order size, from the map the main form's
   * properties[Preorder] input follows (blocks/buy-buttons.liquid).
   * @param {number[]} ids
   */
  #anyPreorder(ids) {
    if (!this.#preorderStates) {
      const map = this.closest('.buy-buttons-block')?.querySelector('product-form-component [data-preorder-variants]');
      try {
        this.#preorderStates = JSON.parse(map?.textContent || '{}');
      } catch (error) {
        this.#preorderStates = {};
      }
    }
    const states = this.#preorderStates ?? {};
    return ids.some((id) => states[id] === true);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const tee = /** @type {HTMLElement | null} */ (input.closest('[data-pack-tee]'));
    if (tee) {
      this.#touched.add(tee.dataset.packTee);
      this.#refreshTee(tee);
    } else if (input.closest('.pack-picker__packs')) {
      this.#pack = Number(input.value) || 1;
      this.#showTees();
    }

    this.#apply();
  };

  /** @param {Event & {detail?: any}} event */
  #onVariantUpdate = (event) => {
    const data = event.detail?.data || {};
    if (data.productId != null && String(data.productId) !== this.dataset.productId) return;

    // After the theme's own handlers have morphed the buttons and replaced the price.
    requestAnimationFrame(() => {
      this.#convert();
      this.#snapshot();
      this.#apply();
    });
  };

  #showTees() {
    let anyOpen = false;

    for (const tee of this.#tees()) {
      const open = Number(tee.dataset.packTee) <= this.#pack;
      if (open && tee.hidden && !this.#touched.has(tee.dataset.packTee)) this.#copyTee1(tee);
      tee.hidden = !open;
      anyOpen ||= open;
    }

    const extras = /** @type {HTMLElement | null} */ (this.querySelector('[data-pack-extras]'));
    if (extras) extras.hidden = !anyOpen;
  }

  /** @param {HTMLElement} tee */
  #copyTee1(tee) {
    const tee1 = this.#variants.find((variant) => String(variant.id) === this.#mainVariantInput()?.value);
    const variant = tee1?.available ? tee1 : this.#variants.find((candidate) => candidate.available);

    if (variant) {
      for (const fieldset of tee.querySelectorAll('fieldset[data-pack-option]')) {
        const value = variant.options[Number(/** @type {HTMLElement} */ (fieldset).dataset.packOption)];
        for (const input of fieldset.querySelectorAll('input')) {
          if (input.value === value) input.checked = true;
        }
      }
    }

    this.#refreshTee(tee);
  }

  /**
   * Legend value and sold-out dimming for one picker. A value is available when some
   * pack-available variant has it together with this picker's other selections; swatches only
   * need the value itself, like the main picker, which never dims colours.
   * @param {HTMLElement} tee
   */
  #refreshTee(tee) {
    const selected = this.#selectedOptions(tee);

    for (const fieldset of tee.querySelectorAll('fieldset[data-pack-option]')) {
      const index = Number(/** @type {HTMLElement} */ (fieldset).dataset.packOption);
      const isSwatch = fieldset.classList.contains('variant-option--swatches');

      const swatchValue = fieldset.querySelector('[data-pack-swatch-value]');
      if (swatchValue) swatchValue.textContent = selected[index] ?? '';

      for (const input of fieldset.querySelectorAll('input')) {
        const available = this.#variants.some(
          (variant) =>
            variant.available &&
            variant.options[index] === input.value &&
            (isSwatch || variant.options.every((value, i) => i === index || value === selected[i]))
        );
        input.dataset.optionAvailable = String(available);
        if (isSwatch) continue;

        const label = input.closest('label');
        if (available) {
          input.removeAttribute('aria-disabled');
          label?.removeAttribute('data-size-soldout');
        } else {
          input.setAttribute('aria-disabled', 'true');
          label?.setAttribute('data-size-soldout', 'true');
        }
      }
    }
  }

  #apply() {
    const main = this.#mainButton();
    const sticky = this.#stickyButton();
    const base = this.#base;

    this.#active = this.#pack > 1 && !!base.main && !base.main.disabled;

    if (this.#active) {
      const ids = this.#packIds(this.#mainVariantInput()?.value ?? '');
      const preorder = !!ids && this.#anyPreorder(ids);
      const label = ids ? this.#packLabel(preorder) : this.dataset.soldOutLabel || '';
      write(main, mainLabel(main), label, !ids, preorder);
      write(sticky, stickyLabel(sticky), label, !ids, preorder);
    } else {
      if (base.main) write(main, mainLabel(main), base.main.label, base.main.disabled, base.main.preorder);
      if (base.sticky) write(sticky, stickyLabel(sticky), base.sticky.label, base.sticky.disabled, base.sticky.preorder);
    }

    document.dispatchEvent(
      new CustomEvent('pack:change', { detail: { productId: this.dataset.productId, pack: this.#pack } })
    );
  }

  /** Captures the theme's own button state. Only called when the pack label isn't on them. */
  #snapshot() {
    const main = this.#mainButton();
    const sticky = this.#stickyButton();

    this.#base = {
      main: main
        ? { label: mainLabel(main)?.textContent ?? '', disabled: main.disabled, preorder: hasPreorderMarker(main) }
        : undefined,
      sticky: sticky
        ? { label: stickyLabel(sticky)?.textContent ?? '', disabled: sticky.disabled, preorder: hasPreorderMarker(sticky) }
        : undefined,
    };
  }

  /**
   * Other currencies: converts the pack amounts and SAVE in this row and in the price line,
   * then reveals them. The price line is replaced with fresh server HTML on every variant change,
   * so this runs again after each one; anything already done is skipped.
   */
  #convert() {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const unitPrice = Number(this.dataset.unitPrice) || 0;
    const roots = [this, ...(this.#section()?.querySelectorAll('.pack-offer') ?? [])];

    for (const root of roots) {
      if (!root.hasAttribute('data-convert') || root.hasAttribute('data-ready')) continue;

      const format = formatter(/** @type {HTMLElement} */ (root).dataset.currency || '');

      for (const item of root.querySelectorAll('[data-pack-cents]')) {
        const { packCents, packQty } = /** @type {HTMLElement} */ (item).dataset;
        // Round to whole minor units first: 8800 * 0.79 is 6952.000000000001.
        const cents = Math.round(Math.round(Number(packCents) * rate) / 100) * 100;
        setText(item.querySelector('[data-pack-amount]'), format(cents));

        const save = /** @type {HTMLElement | null} */ (item.querySelector('[data-pack-save]'));
        if (save) {
          const saved = Number(packQty) * unitPrice - cents;
          save.hidden = !(saved > 0);
          setText(save.querySelector('[data-pack-save-amount]'), format(saved));
        }
      }

      root.setAttribute('data-ready', '');
    }
  }

  /** @param {boolean} preorder */
  #packLabel(preorder) {
    const input = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`.pack-picker__packs input[value="${this.#pack}"]`)
    );
    const amount = input?.closest('label')?.querySelector('[data-pack-amount]')?.textContent?.trim() ?? '';
    const prefix = preorder ? input?.dataset.preorderLabelPrefix : input?.dataset.labelPrefix;
    return (prefix ?? '') + amount;
  }

  /** @param {HTMLElement} tee */
  #selectedOptions(tee) {
    /** @type {Array<string | undefined>} */
    const selected = [];
    for (const option of tee.querySelectorAll('[data-pack-option]')) {
      const element = /** @type {HTMLElement} */ (option);
      const checked = /** @type {HTMLInputElement | null} */ (element.querySelector('input:checked'));
      selected[Number(element.dataset.packOption)] = element.matches('fieldset') ? checked?.value : element.dataset.value;
    }
    return selected;
  }

  /** @param {HTMLElement} tee */
  #teeVariant(tee) {
    const selected = this.#selectedOptions(tee);
    return this.#variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
  }

  #tees() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-pack-tee]')));
  }

  #openTees() {
    return this.#tees().filter((tee) => Number(tee.dataset.packTee) <= this.#pack);
  }

  #section() {
    return this.closest('.shopify-section');
  }

  #mainButton() {
    return /** @type {HTMLButtonElement | null} */ (
      this.closest('.buy-buttons-block')?.querySelector('product-form-component [ref="addToCartButton"]') ?? null
    );
  }

  #mainVariantInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.closest('.buy-buttons-block')?.querySelector('product-form-component input[name="id"]') ?? null
    );
  }

  #stickyButton() {
    return /** @type {HTMLButtonElement | null} */ (
      this.#section()?.querySelector(
        `sticky-add-to-cart[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`
      ) ?? null
    );
  }
}

/** @typedef {{label: string, disabled: boolean, preorder: boolean}} ButtonState */

/** The text span inside snippets/add-to-cart-button.liquid. @param {HTMLElement | null} button */
function mainLabel(button) {
  return button?.querySelector('.add-to-cart-text__content > span > span') ?? null;
}

/** The text span inside the sticky bar's button, before its "(qty)". @param {HTMLElement | null} button */
function stickyLabel(button) {
  return button?.querySelector('.add-to-cart-text__content > span:first-child') ?? null;
}

/**
 * The pre-order marker the theme's orange button styling keys on (assets/themefew.css). It sits
 * on the label wrapper, not the button; the mobile button copies it from there.
 * @param {HTMLElement | null} button
 */
function hasPreorderMarker(button) {
  return button?.querySelector('.add-to-cart-text__content')?.getAttribute('data-preorder') === 'true';
}

/**
 * @param {HTMLButtonElement | null} button
 * @param {Element | null} labelElement
 * @param {string} label
 * @param {boolean} disabled
 * @param {boolean} preorder
 */
function write(button, labelElement, label, disabled, preorder) {
  if (!button) return;
  setText(labelElement, label);
  if (button.disabled !== disabled) button.disabled = disabled;

  const content = button.querySelector('.add-to-cart-text__content');
  if (content && hasPreorderMarker(button) !== preorder) {
    if (preorder) content.setAttribute('data-preorder', 'true');
    else content.removeAttribute('data-preorder');
  }
}

/** @param {Element | null} element @param {string} text */
function setText(element, text) {
  if (element && element.textContent !== text) element.textContent = text;
}

/** Whole units, like assets/free-shipping-bar.js. @param {string} currency */
function formatter(currency) {
  const format = new Intl.NumberFormat(document.documentElement.lang || undefined, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return (/** @type {number} */ cents) => format.format(cents / 100);
}

if (!customElements.get('pack-picker')) {
  customElements.define('pack-picker', PackPicker);
}
